import { useUser } from "@clerk/expo";
import { Plus, Shuffle, Trash2, X } from "lucide-react-native";
import { FC, useEffect } from "react";

import {
  Button,
  ButtonIcon,
  ButtonText,
  DropdownPicker,
  HStack,
  Input,
  InputField,
  Pressable,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";
import { Assignment, Question, QuestionDraft, Rule } from "@/types/resources";
import { RuleOperator } from "@/types/utils";
import {
  defaultOperatorForQuestionType,
  hasApplicableRules,
  operatorsForQuestionType,
  useOperatorLabels,
} from "../rules";

const MAX_GROUPS = 15;
const MIN_GROUPS = 2;

const DEFAULT_ASSIGNMENT: Assignment = { mappings: [], method: "least_fill" };

interface CategoryAssignmentQuestionEditorProps {
  allQuestions?: Question[];
  currentQuestionOrder?: number;
  disabled?: boolean;
  onChange: (question: QuestionDraft<"category_assignment">) => void;
  question: QuestionDraft<"category_assignment">;
}

const CategoryAssignmentQuestionEditor: FC<
  CategoryAssignmentQuestionEditorProps
> = ({
  allQuestions = [],
  currentQuestionOrder,
  disabled = false,
  onChange,
  question,
}) => {
  const { i18n } = useLocalization();
  const operatorLabels = useOperatorLabels();
  const { user } = useUser();
  const isAdmin = user?.publicMetadata?.admin === true;

  const rules = question.rules || [];

  const optionSpecificRules = rules.filter(
    (r) => r.option_index !== null && r.source_question_id === null
  );

  // ── Assignment (mandatory) ───────────────────────────────
  // A category-assignment question is meaningless without an assignment
  // method, so materialize a default the moment the editor mounts on a
  // legacy question that has none.
  useEffect(() => {
    if (!question.assignment) {
      onChange({ ...question, assignment: DEFAULT_ASSIGNMENT });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Legacy mappings predate the operator field and studies imported without one
  // default to `eq`, which the API rejects for multiple-choice sources. Backfill
  // (and coerce mismatched) operators to the source type's default so the value
  // shown in the dropdown is the value actually saved.
  useEffect(() => {
    const maps = question.assignment?.mappings;
    if (!maps?.length) return;
    let changed = false;
    const next = maps.map((m) => {
      const source = allQuestions.find((q) => q.id === m.source_question_id);
      if (!source) return m;
      const allowed = operatorsForQuestionType(source.type);
      if (m.operator && allowed.includes(m.operator)) return m;
      changed = true;
      return { ...m, operator: allowed[0] };
    });
    if (changed) {
      onChange({
        ...question,
        assignment: { ...question.assignment!, mappings: next },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.assignment?.mappings, allQuestions]);

  const assignment = question.assignment ?? DEFAULT_ASSIGNMENT;
  const mappings = assignment.mappings;

  // Eligibility conditions can key off any prior choice-style answer.
  const eligibilitySources = allQuestions
    .filter(
      (q) =>
        currentQuestionOrder !== undefined &&
        q.order < currentQuestionOrder &&
        (q.type === "single_choice" ||
          q.type === "multiple_choice" ||
          q.type === "category_assignment")
    )
    .sort((a, b) => a.order - b.order);

  const setMethod = (method: Assignment["method"]) => {
    onChange({ ...question, assignment: { ...assignment, method } });
  };

  const setMappings = (next: Assignment["mappings"]) => {
    onChange({ ...question, assignment: { ...assignment, mappings: next } });
  };

  const conditionsForTrack = (trackIndex: number) =>
    mappings
      .map((m, absIndex) => ({ absIndex, m }))
      .filter(({ m }) => m.option_index === trackIndex);

  // A given (track, source question, option) combo is only meaningful once —
  // mirror the skip-rules editor and disable answers already claimed by a
  // sibling condition on the same track.
  const usedOptionsForCondition = (
    trackIndex: number,
    sourceQuestionId: string,
    excludeAbsIndex: number
  ): Set<number> => {
    const used = new Set<number>();
    mappings.forEach((m, i) => {
      if (
        i !== excludeAbsIndex &&
        m.option_index === trackIndex &&
        m.source_question_id === sourceQuestionId
      ) {
        used.add(m.source_option_index);
      }
    });
    return used;
  };

  const availableOptionsForCondition = (
    trackIndex: number,
    sourceQuestionId: string,
    excludeAbsIndex: number
  ): number[] => {
    const source = allQuestions.find((q) => q.id === sourceQuestionId);
    const count = source?.options.length ?? 0;
    const used = usedOptionsForCondition(
      trackIndex,
      sourceQuestionId,
      excludeAbsIndex
    );
    const out: number[] = [];
    for (let i = 0; i < count; i++) if (!used.has(i)) out.push(i);
    return out;
  };

  const firstSourceWithAvailableOption = (
    trackIndex: number
  ): { option: number; questionId: string } | null => {
    for (const q of eligibilitySources) {
      const available = availableOptionsForCondition(trackIndex, q.id, -1);
      if (available.length > 0)
        return { option: available[0], questionId: q.id };
    }
    return null;
  };

  const operatorForSource = (sourceQuestionId: string): RuleOperator => {
    const source = allQuestions.find((q) => q.id === sourceQuestionId);
    return source
      ? defaultOperatorForQuestionType(source.type)
      : RuleOperator.Equal;
  };

  const addCondition = (trackIndex: number) => {
    const next = firstSourceWithAvailableOption(trackIndex);
    if (!next) return;
    setMappings([
      ...mappings,
      {
        operator: operatorForSource(next.questionId),
        option_index: trackIndex,
        source_option_index: next.option,
        source_question_id: next.questionId,
      },
    ]);
  };

  const setConditionSource = (absIndex: number, sourceQuestionId: string) => {
    setMappings(
      mappings.map((m, i) => {
        if (i !== absIndex) return m;
        const available = availableOptionsForCondition(
          m.option_index,
          sourceQuestionId,
          absIndex
        );
        return {
          ...m,
          operator: operatorForSource(sourceQuestionId),
          source_option_index: available[0] ?? 0,
          source_question_id: sourceQuestionId,
        };
      })
    );
  };

  const setConditionOption = (absIndex: number, sourceOptionIndex: number) => {
    setMappings(
      mappings.map((m, i) =>
        i === absIndex ? { ...m, source_option_index: sourceOptionIndex } : m
      )
    );
  };

  const setConditionOperator = (absIndex: number, operator: RuleOperator) => {
    setMappings(
      mappings.map((m, i) => (i === absIndex ? { ...m, operator } : m))
    );
  };

  const removeCondition = (absIndex: number) => {
    setMappings(mappings.filter((_, i) => i !== absIndex));
  };

  const questionOptionLabel = (order?: number, text?: string): string =>
    `${order}. ${text?.trim() || i18n.t("questions.untitled")}`;

  // Default track label for a positional slot ("Track 1..N"). Used as the
  // seed name for new tracks and as the input placeholder, but names are
  // freely editable from there.
  const trackName = (index: number): string =>
    i18n.t("questions.categoryAssignment.trackLabel", { number: index + 1 });

  const setTrackName = (index: number, value: string) => {
    onChange({
      ...question,
      options: question.options.map((o, i) => (i === index ? value : o)),
    });
  };

  const addGroup = () => {
    if (isAdmin || question.options.length < MAX_GROUPS) {
      onChange({
        ...question,
        options: [...question.options, trackName(question.options.length)],
      });
    }
  };

  const removeGroup = (index: number) => {
    if (question.options.length <= MIN_GROUPS) return;

    // Drop the removed track. Tracks still carrying their default "Track N"
    // label get renumbered to their new position; custom-named tracks are
    // left untouched.
    const newOptions = question.options
      .filter((_, i) => i !== index)
      .map((name, newIndex) => {
        const oldIndex = newIndex < index ? newIndex : newIndex + 1;
        return name === trackName(oldIndex) ? trackName(newIndex) : name;
      });

    // Option-specific skip rules and eligibility mappings key off the track
    // index, so drop the removed track's and shift the ones above it down.
    const shift = (optionIndex: number | null): number | null =>
      optionIndex !== null && optionIndex > index
        ? optionIndex - 1
        : optionIndex;

    const newRules = rules
      .filter(
        (rule) =>
          !(rule.option_index === index && rule.source_question_id === null)
      )
      .map((rule) => ({
        ...rule,
        option_index:
          rule.source_question_id === null
            ? shift(rule.option_index)
            : rule.option_index,
      }));

    const newMappings = mappings
      .filter((m) => m.option_index !== index)
      .map((m) => ({ ...m, option_index: shift(m.option_index) as number }));

    onChange({
      ...question,
      assignment: { ...assignment, mappings: newMappings },
      options: newOptions,
      rules: newRules,
    });
  };

  const getRuleForOption = (optionIndex: number): Rule | undefined =>
    optionSpecificRules.find((rule) => rule.option_index === optionIndex);

  const getTargetQuestionIdForOption = (optionIndex: number): string | null => {
    const rule = getRuleForOption(optionIndex);
    if (!rule) return null;
    if (rule.target_question_id === null) return "__END_SURVEY__";
    return rule.target_question_id;
  };

  // Upsert the track's destination ("start of the track") rule. Every track
  // routes to a single later question (or the survey end) once assigned, so
  // this is a plain eq rule keyed off the track index.
  const setTrackDestination = (trackIndex: number, value: string) => {
    const existingRule = getRuleForOption(trackIndex);
    const otherRules = rules.filter(
      (r) => !(r.option_index === trackIndex && r.source_question_id === null)
    );
    const newRule: Rule = {
      created_at: existingRule?.created_at || "",
      id: existingRule?.id || "",
      operator: RuleOperator.Equal,
      option_index: trackIndex,
      probability: null,
      source_question_id: null,
      target_question_id: value === "__END_SURVEY__" ? null : value,
      updated_at: new Date().toISOString(),
    };
    onChange({ ...question, rules: [...otherRules, newRule] });
  };

  // Routing is only possible (and required) when there is a later question
  // to send the track to.
  const canShowRules = hasApplicableRules(allQuestions, currentQuestionOrder);

  const destinationItems = [
    { label: i18n.t("questions.endSurvey"), value: "__END_SURVEY__" },
    ...allQuestions
      .filter(
        (q) =>
          currentQuestionOrder !== undefined && q.order > currentQuestionOrder
      )
      .sort((a, b) => a.order - b.order)
      .map((q) => ({ label: `${q.order}. ${q.text}`, value: q.id })),
  ];

  return (
    <VStack className="gap-6">
      {/* Static title + explanation */}
      <VStack className="gap-1">
        <HStack className="items-center gap-1">
          <Text className="text-sm font-semibold text-typography-800">
            {i18n.t("questions.categoryAssignment.title")}
          </Text>
        </HStack>
        <Text className="text-sm text-typography-500">
          {i18n.t("questions.categoryAssignment.explanation")}
        </Text>
      </VStack>

      {/* Assignment method (mandatory) */}
      <VStack className="gap-2">
        <VStack className="gap-2.5">
          {(["least_fill", "random"] as const).map((m) => {
            const selected = assignment.method === m;
            return (
              <Pressable
                key={m}
                disabled={disabled}
                onPress={() => setMethod(m)}
              >
                <HStack className="items-start gap-3">
                  <VStack
                    className={`mt-0.5 h-4 w-4 items-center justify-center rounded-full border-2 ${
                      selected ? "border-primary-500" : "border-outline-300"
                    }`}
                  >
                    {selected && (
                      <VStack className="h-2 w-2 rounded-full bg-primary-500" />
                    )}
                  </VStack>
                  <VStack className="flex-1 gap-0.5">
                    <Text
                      className={`text-sm font-medium ${
                        selected ? "text-typography-900" : "text-typography-700"
                      }`}
                    >
                      {m === "least_fill"
                        ? i18n.t("questions.categoryAssignment.leastFill")
                        : i18n.t("questions.categoryAssignment.random")}
                    </Text>
                    <Text className="text-xs text-typography-400">
                      {m === "least_fill"
                        ? i18n.t("questions.categoryAssignment.leastFillHint")
                        : i18n.t("questions.categoryAssignment.randomHint")}
                    </Text>
                  </VStack>
                </HStack>
              </Pressable>
            );
          })}
        </VStack>
      </VStack>

      {/* Tracks */}
      <VStack className="gap-3">
        <Text className="text-sm font-semibold text-typography-800">
          {i18n.t("questions.categoryAssignment.tracksLabel")}
        </Text>
        {eligibilitySources.length > 0 && (
          <Text className="text-xs text-typography-400">
            {i18n.t("questions.categoryAssignment.eligibilityHint")}
          </Text>
        )}
        <VStack className="gap-3">
          {question.options.map((option, index) => {
            const conditions = conditionsForTrack(index);
            return (
              <VStack
                key={index}
                className="gap-3 rounded-xl border border-outline-200 bg-background-0 p-3"
              >
                <HStack className="items-center gap-3">
                  <Shuffle className="text-typography-400" size={16} />
                  <Input
                    className="flex-1 border border-outline-200 rounded-xl bg-background-50"
                    isDisabled={disabled}
                  >
                    <InputField
                      onChangeText={(value) => setTrackName(index, value)}
                      placeholder={trackName(index)}
                      value={option}
                    />
                  </Input>
                  {question.options.length > MIN_GROUPS && (
                    <Pressable
                      className="p-1.5 rounded-md hover:bg-error-50"
                      disabled={disabled}
                      onPress={() => removeGroup(index)}
                    >
                      <Trash2 className="text-error-500" size={16} />
                    </Pressable>
                  )}
                </HStack>

                {canShowRules && (
                  <VStack className="gap-1">
                    <Text className="text-xs font-medium uppercase tracking-wide text-typography-400">
                      {i18n.t("questions.categoryAssignment.trackStart")}
                    </Text>
                    <DropdownPicker
                      className="w-full"
                      isDisabled={disabled}
                      items={destinationItems}
                      onChange={(v) => setTrackDestination(index, v)}
                      placeholder={i18n.t(
                        "questions.categoryAssignment.setDestination"
                      )}
                      value={getTargetQuestionIdForOption(index)}
                    />
                  </VStack>
                )}

                {eligibilitySources.length > 0 && (
                  <VStack className="gap-1.5">
                    <Text className="text-xs font-medium uppercase tracking-wide text-typography-400">
                      {i18n.t("questions.categoryAssignment.eligibleWhen")}
                    </Text>
                    {conditions.map(({ absIndex, m }) => {
                      const sourceQ = allQuestions.find(
                        (q) => q.id === m.source_question_id
                      );
                      const sourceOptions = sourceQ?.options ?? [];
                      const usedOptions = usedOptionsForCondition(
                        index,
                        m.source_question_id,
                        absIndex
                      );
                      const sourceItems = eligibilitySources.map((q) => ({
                        isDisabled:
                          availableOptionsForCondition(index, q.id, absIndex)
                            .length === 0,
                        label: questionOptionLabel(q.order, q.text),
                        value: q.id,
                      }));
                      const optionItems = sourceOptions.map((opt, i) => ({
                        isDisabled: usedOptions.has(i),
                        label:
                          opt || i18n.t("questions.option", { number: i + 1 }),
                        value: String(i),
                      }));
                      return (
                        <HStack key={absIndex} className="items-center gap-2">
                          <DropdownPicker
                            className="flex-1"
                            isDisabled={disabled}
                            items={sourceItems}
                            onChange={(v) => setConditionSource(absIndex, v)}
                            placeholder={i18n.t("questions.selectOption")}
                            selectKey={`elig-src-${absIndex}-${sourceItems
                              .map((it) => `${it.value}:${it.isDisabled}`)
                              .join(",")}`}
                            value={m.source_question_id}
                          />
                          <DropdownPicker
                            className="w-40 shrink-0"
                            isDisabled={disabled}
                            items={operatorsForQuestionType(
                              sourceQ?.type ?? "single_choice"
                            ).map((op) => ({
                              label: operatorLabels[op],
                              value: op,
                            }))}
                            onChange={(v) =>
                              setConditionOperator(absIndex, v as RuleOperator)
                            }
                            placeholder={i18n.t("questions.selectOption")}
                            selectKey={`elig-op-${absIndex}-${m.source_question_id}`}
                            value={
                              m.operator ??
                              defaultOperatorForQuestionType(
                                sourceQ?.type ?? "single_choice"
                              )
                            }
                          />
                          <DropdownPicker
                            className="flex-1"
                            isDisabled={disabled}
                            items={optionItems}
                            onChange={(v) =>
                              setConditionOption(absIndex, parseInt(v, 10))
                            }
                            placeholder={i18n.t("questions.selectOption")}
                            selectKey={`elig-opt-${absIndex}-${m.source_question_id}-${optionItems
                              .map((it) => `${it.value}:${it.isDisabled}`)
                              .join(",")}`}
                            value={String(m.source_option_index)}
                          />
                          <Pressable
                            className="p-1"
                            disabled={disabled}
                            onPress={() => removeCondition(absIndex)}
                          >
                            <X className="text-typography-400" size={16} />
                          </Pressable>
                        </HStack>
                      );
                    })}
                    {firstSourceWithAvailableOption(index) !== null && (
                      <Button
                        className="self-start"
                        isDisabled={disabled}
                        onPress={() => addCondition(index)}
                        variant="link"
                      >
                        <ButtonIcon as={Plus} size="sm" />
                        <ButtonText>
                          {i18n.t("questions.categoryAssignment.addCondition")}
                        </ButtonText>
                      </Button>
                    )}
                  </VStack>
                )}
              </VStack>
            );
          })}

          {(isAdmin || question.options.length < MAX_GROUPS) && (
            <Button
              className="self-start rounded-lg border-dashed"
              isDisabled={disabled}
              onPress={addGroup}
              size="sm"
              variant="outline"
            >
              <Plus color="rgb(var(--color-typography-600))" size={14} />
              <ButtonText className="text-sm text-typography-600 ml-1">
                {i18n.t("questions.categoryAssignment.addTrack")}
              </ButtonText>
            </Button>
          )}
        </VStack>
      </VStack>
    </VStack>
  );
};

export default CategoryAssignmentQuestionEditor;
